import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';
import arcaLogo from '../assets/arca_logo.png';
import PublicLayout from '../components/public/PublicLayout';

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [token, setToken] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
    } else {
      setMessage('Invalid or missing reset token.');
      setMessageType('error');
    }
  }, [searchParams]);

  // Check password requirements in real-time
  const checkPasswordRequirements = (pwd) => {
    const requirements = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[@$!%*?&]/.test(pwd)
    };

    setPasswordRequirements(requirements);

    // Calculate strength (0-5 based on met requirements)
    const strength = Object.values(requirements).filter(Boolean).length;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordRequirements(newPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');

    if (!password || !confirmPassword) {
      setMessage('Please enter and confirm your password.');
      setMessageType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      setMessageType('error');
      return;
    }

    if (password.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);

    try {
      const adminResponse = await fetch(`${process.env.REACT_APP_API_URL}/admin/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      let data, responseOk;

      if (adminResponse.ok) {
        data = await adminResponse.json();
        responseOk = adminResponse.ok;
      } else {
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        });

        data = await userResponse.json();
        responseOk = userResponse.ok;
      }

      if (responseOk) {
        setMessage('Password reset successfully! Redirecting to login...');
        setMessageType('success');
        toast.success('Password reset successfully! Redirecting to login...');

        localStorage.removeItem('arcaUser');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('userToken');
        localStorage.removeItem('directorToken');

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        const errorMsg = data.message || 'Failed to reset password. Please try again.';
        setMessage(errorMsg);
        setMessageType('error');
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMsg = error.message && error.message.includes('fetch')
        ? 'Unable to connect to server. Please check your connection and try again.'
        : 'An error occurred. Please try again later.';
      setMessage(errorMsg);
      setMessageType('error');
      toast.error(errorMsg);
    }

    setIsSubmitting(false);
  };

  return (
    <PublicLayout>
      <div className="py-5" style={{ minHeight: '80vh', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
              <div className="card shadow-lg border-0" style={{ borderRadius: '12px' }}>
                <div className="card-body p-4 p-md-5">

                  <div className="text-center mb-4">
                    <img
                      src={arcaLogo}
                      alt="ARCA Logo"
                      style={{ width: '180px', height: 'auto', marginBottom: '10px' }}
                    />
                  </div>

                  <h2 className="text-center h4 fw-semibold text-dark mb-2">Reset Your Password</h2>
                  <p className="text-center text-muted mb-4 small">Create a new password for your account</p>

                  {message && (
                    <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
                      {message}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>

                    <div className="mb-3">
                      <label htmlFor="password" className="form-label fw-medium">New Password</label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          className="form-control form-control-lg"
                          value={password}
                          onChange={handlePasswordChange}
                          required
                          placeholder="Enter your new password"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute top-50 end-0 translate-middle-y"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ textDecoration: 'none', color: '#6B8E23', zIndex: 10 }}
                          tabIndex="-1"
                        >
                          {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                        </button>
                      </div>

                      {/* Password Strength Indicator */}
                      {password && (
                        <div className="mt-2">
                          <div className="progress" style={{ height: '5px' }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{
                                width: `${(passwordStrength / 5) * 100}%`,
                                backgroundColor:
                                  passwordStrength <= 2
                                    ? '#dc3545'
                                    : passwordStrength <= 3
                                      ? '#ffc107'
                                      : passwordStrength <= 4
                                        ? '#17a2b8'
                                        : '#6B8E23'
                              }}
                              aria-valuenow={passwordStrength}
                              aria-valuemin="0"
                              aria-valuemax="5"
                            ></div>
                          </div>
                          <small className="text-muted">
                            Strength:{' '}
                            <span
                              style={{
                                fontWeight: 'bold',
                                color:
                                  passwordStrength <= 2
                                    ? '#dc3545'
                                    : passwordStrength <= 3
                                      ? '#ffc107'
                                      : passwordStrength <= 4
                                        ? '#17a2b8'
                                        : '#6B8E23'
                              }}
                            >
                              {passwordStrength <= 2
                                ? 'Weak'
                                : passwordStrength <= 3
                                  ? 'Fair'
                                  : passwordStrength <= 4
                                    ? 'Good'
                                    : 'Strong'}
                            </span>
                          </small>
                        </div>
                      )}

                      {/* Password Requirements Checklist */}
                      {password && (
                        <div className="mt-2">
                          <small className="text-muted d-block mb-1">Password must contain:</small>
                          <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                            <li style={{ color: passwordRequirements.length ? '#6B8E23' : '#6c757d' }}>
                              <span className="me-2">{passwordRequirements.length ? '✓' : '○'}</span>
                              At least 8 characters
                            </li>
                            <li style={{ color: passwordRequirements.uppercase ? '#6B8E23' : '#6c757d' }}>
                              <span className="me-2">{passwordRequirements.uppercase ? '✓' : '○'}</span>
                              One uppercase letter (A-Z)
                            </li>
                            <li style={{ color: passwordRequirements.lowercase ? '#6B8E23' : '#6c757d' }}>
                              <span className="me-2">{passwordRequirements.lowercase ? '✓' : '○'}</span>
                              One lowercase letter (a-z)
                            </li>
                            <li style={{ color: passwordRequirements.number ? '#6B8E23' : '#6c757d' }}>
                              <span className="me-2">{passwordRequirements.number ? '✓' : '○'}</span>
                              One number (0-9)
                            </li>
                            <li style={{ color: passwordRequirements.special ? '#6B8E23' : '#6c757d' }}>
                              <span className="me-2">{passwordRequirements.special ? '✓' : '○'}</span>
                              One special character (@$!%*?&)
                            </li>
                          </ul>
                        </div>
                      )}

                      <small className="form-text text-muted d-block mt-2">
                        Password must be at least 8 characters and include uppercase, lowercase, number, and special character (@$!%*?&).
                      </small>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="confirmPassword" className="form-label fw-medium">Confirm Password</label>
                      <div className="position-relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          className="form-control form-control-lg"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          placeholder="Confirm your new password"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute top-50 end-0 translate-middle-y"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{ textDecoration: 'none', color: '#6B8E23', zIndex: 10 }}
                          tabIndex="-1"
                        >
                          {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div className="d-grid mb-3">
                      <button
                        type="submit"
                        className="btn btn-lg"
                        style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23', color: 'white' }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Resetting Password...
                          </>
                        ) : (
                          'Reset Password'
                        )}
                      </button>
                    </div>
                  </form>

                  <div className="text-center mt-3">
                    <Link
                      to="/login"
                      className="btn btn-link text-decoration-none"
                      style={{ color: '#6B8E23' }}
                    >
                      <FaArrowLeft className="me-2" />
                      Back to Login
                    </Link>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PasswordReset;
